/* eslint-disable no-magic-numbers */

// Constants for the interactive map component
export const MAP_CONSTANTS = {
  HEADER_HEIGHT: 40, // Updated to match design specs
  MIN_WIDTH: 200,
  MIN_HEIGHT: 150,
  MAX_WIDTH: 800,
  MAX_HEIGHT: 600,
  MIN_ZOOM: 1.0, // Match original
  MAX_ZOOM: 8.0, // Match original
  ZOOM_SENSITIVITY: 0.001,
  STATION_RADIUS: 5, // Match original exactly
  STATION_CLICK_RADIUS: 15, // Match original
  CLICK_TOLERANCE: 8, // Reduced for more precise hover detection
  // Header icon layout constants (matching Tailwind classes: w-6 h-6, space-x-1)
  HEADER_ICON_SIZE: 24, // Icon size in pixels (w-6 h-6)
  HEADER_ICON_GAP: 4, // Gap between icons in pixels (space-x-1, Tailwind spacing unit)
  MAX_HEADER_ICONS: 3, // Maximum number of icons that can appear (home, undo, expand)
  // Animation constants
  BASE_PULSE_SPEED: 0.0015, // Base speed for spotlight pulse animation
  // Constraint buffer constants
  EDGE_BUFFER: 24, // Buffer from edges - aligned with ActionsToolBar (top-6 = 24px)
  // Positioning constants
  REFERENCE_WIDTH: 1920, // Standard HD resolution width for normalizing positions
  REFERENCE_HEIGHT: 1080, // Standard HD resolution height for normalizing positions
  DEFAULT_EDGE_BUFFER: 20, // Standard buffer for map positioning from viewport edges
  SIDE_EDGE_BUFFER: 10, // Buffer for left, right, and top edges during constraint calculations
  BOTTOM_EDGE_BUFFER: 60, // Larger buffer for bottom edge to accommodate 360 viewer UI elements
  // Mathematical constants
  DEGREES_IN_CIRCLE: 180, // Degrees in a semicircle
  DEGREES_TO_RADIANS_FACTOR: Math.PI / 180, // Conversion factor: multiply degrees by this to get radians
  RADIANS_TO_DEGREES_FACTOR: 180 / Math.PI, // Conversion factor: multiply radians by this to get degrees
  // Standalone icon clustering (screen-space px)
  PHOTO360_CLUSTER_RADIUS_PX: 18, // Screen-space px; photo360 icons within this distance of a rep merge into one cluster.
  ISSUE_MARKER_CLUSTER_RADIUS_PX: 0, // Screen-space px; 0 = only exact-pixel overlaps cluster for issue markers.
  SHOW_CLUSTER_COUNT_PILL: false, // When false, overlapping icons still cluster but no count pill is drawn.
  CLUSTER_PILL_COMPACT_MIN_DIMENSION_PX: 700, // When min(width, height) * zoom is below this, pill shows "+" instead of a numeric count (or "99+").
  BADGE_COUNT_CAP: 999, // Max cluster count shown in pill before displaying "999+".
} as const;

/**
 * Z-index layering constants for the map component
 * Ensures consistent stacking order across all map elements
 */
export const MAP_Z_INDEX = {
  CANVAS_LAYER_BASE: 1, // Base canvas layer (main canvas)
  CANVAS_LAYER_DYNAMIC: 2, // Dynamic canvas layer (current point, spotlight)
  CONTROLS: 10, // Map controls (zoom, reset buttons)
  MAP_CONTAINER: 100, // Main map container
  DROPDOWN_BACKDROP: 200, // Dropdown backdrop overlay
  DROPDOWN_MENU: 201, // Dropdown menu content
} as const;

// Default styling configurations matching original Maya MiniMap exactly
export const DEFAULT_POINT_STYLES = {
  default: {
    color: '#89C6ED', // Original light blue
    size: MAP_CONSTANTS.STATION_RADIUS,
    shape: 'circle',
    borderColor: '#4DA2D3', // Original border color
    borderWidth: 2, // Original lineWidth
  },
  current: {
    color: '#217CBB', // Original darker blue for current
    size: 11, // Larger prominent size for current point (original MiniMap style)
    shape: 'circle',
    borderColor: 'white', // White border for prominence (original MiniMap style)
    borderWidth: 3, // Thicker border for prominence (original MiniMap style)
  },
  first: {
    color: '#217CBB', // Same as current for prominence
    size: 13, // Larger size for video360 icon visibility
    shape: 'circle',
    borderWidth: 0, // No border for icon points
  },
  last: {
    color: '#89C6ED', // Same as default
    size: MAP_CONSTANTS.STATION_RADIUS,
    shape: 'circle',
    borderColor: '#4DA2D3', // Original border color
    borderWidth: 2, // Original lineWidth
  },
  hover: {
    color: '#217CBB', // Same as current
    size: MAP_CONSTANTS.STATION_RADIUS,
    shape: 'circle',
    borderColor: '#4DA2D3', // Original border color
    borderWidth: 2, // Original lineWidth
    // Default "move up" hover effect
    shadowOffset: { x: 0, y: -3 }, // Move up 3 pixels
    shadowColor: 'rgba(0, 0, 0, 0.3)', // Subtle dark shadow
    shadowBlur: 6, // Soft shadow blur
  },
  standaloneVideo: {
    color: '#FF6B6B', // Red color for video captures
    size: 6, // Slightly larger than default points
    shape: 'square',
    borderColor: '#CC5555', // Darker red border
    borderWidth: 2,
  },
  standalonePhoto360: {
    color: '#217CBB', // Same blue as flag icons
    size: 13, // Same size as flag icons for icon visibility
    shape: 'circle',
    borderWidth: 0, // No border for clean icon appearance
  },
  standaloneIssueMarker: {
    color: '#0D6EFD', // Blue color for issue markers
    size: 13, // Same size as flag icons for icon visibility
    shape: 'circle',
    borderWidth: 0, // No border for clean icon appearance
  },
} as const;

export const DEFAULT_TRAJECTORY_STYLE = {
  color: 'rgba(137, 198, 237, 0.9)', // Original trajectory color
  width: 3, // Original trajectory width
  opacity: 0.9,
  endCaps: 'round',
  joinStyle: 'round',
} as const;

// Default spotlight color (Trimble blue) - single source of truth
export const DEFAULT_SPOTLIGHT_COLOR = '#217CBB';

export const DEFAULT_SPOTLIGHT_STYLE = {
  enabled: false, // Disabled by default
  color: DEFAULT_SPOTLIGHT_COLOR, // Matching current point color
  opacity: 0.8,
  size: 1.0, // 1.0 = default size (now matches original MiniMap with 1.5x multiplier)
  angle: 75, // Default FOV angle in degrees (matching original)
  animation: {
    enabled: true,
    speed: 1.0, // Default animation speed
    intensity: 0.8, // 80% intensity range
    minOpacity: 0.15, // Minimum opacity during pulse
    maxOpacity: 1.0, // Maximum opacity during pulse
  },
};

// Default map size
// Reason of these dimensions are not arbitrary, they are from the UX designs
export const DEFAULT_MAP_SIZE = {
  width: 362,
  height: 261,
} as const;

// Constants for expanded map functionality
export const EXPANDED_MAP_CONSTANTS = {
  DEFAULT_EXPANDED_WIDTH: 1154,
  DEFAULT_EXPANDED_HEIGHT: 762,
  DIVISION_FACTOR: 2,
  FALLBACK_WIDTH: 400,
  FALLBACK_HEIGHT: 300,
  ZOOM_FACTOR: 0.7,
  ZOOM_MULTIPLIER: 8.0,
  PADDING: 40,
  ZOOM_STEP: 0.25,
} as const;
